import { injectable } from 'inversify';

import {
  DraggableNewsItemType,
  GridSlot,
  TopNewsWidgetType,
  ProcessCalculatedSlotsParams,
  ProcessTransformedSlotsResult,
} from '../../types';

@injectable()
export class SettingsTopNewsMapper {
  processCalculatedSlots({
    originalSlots,
    sourceIndex,
    targetIndex,
    activeId,
    availableNews,
  }: ProcessCalculatedSlotsParams): GridSlot[] {
    if (sourceIndex === targetIndex) {
      return originalSlots;
    }

    const newSlots = [...originalSlots];

    if (sourceIndex !== -1) {
      [newSlots[sourceIndex], newSlots[targetIndex]] = [
        newSlots[targetIndex],
        newSlots[sourceIndex],
      ];
    } else {
      const draggedItem = availableNews.find((item) => item.id === activeId);
      if (draggedItem && !newSlots[targetIndex]?.newsItem) {
        newSlots[targetIndex] = { newsItem: draggedItem };
      }
    }

    return newSlots;
  }

  processGridLayoutByType(type: TopNewsWidgetType) {
    const baseLayouts = {
      type_1_4: {
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gridTemplateAreas: '"big small1 small2" "big small3 small4"',
        areas: ['big', 'small1', 'small2', 'small3', 'small4'],
      },
      type_4: {
        height: '196px',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gridTemplateRows: '1fr',
        alignItems: 'center',
        justifyContent: 'center',
        areas: Array(this.processSlotCountFromLayoutType('type_4')).fill(
          'auto',
        ),
      },
    } as const;

    return baseLayouts[type];
  }

  processSlotCountFromLayoutType(layoutType: string): number {
    const numbers = layoutType.match(/\d+/g);

    if (!numbers) {
      return 0;
    }

    return numbers.reduce((sum, num) => sum + parseInt(num, 10), 0);
  }

  processTransformedSlots(
    newSize: TopNewsWidgetType,
    currentSize: TopNewsWidgetType,
    currentSlots: GridSlot[],
  ): ProcessTransformedSlotsResult {
    const currentSlotCount = this.processSlotCountFromLayoutType(currentSize);
    const createSlot = (slot: GridSlot) => ({ newsItem: slot.newsItem });

    let removedItem: DraggableNewsItemType | undefined;

    if (newSize === 'type_4' && currentSize === 'type_1_4') {
      if (currentSlots[0]?.newsItem) {
        removedItem = currentSlots[0].newsItem;
      }

      const newSlots = currentSlots.slice(1, currentSlotCount).map(createSlot);

      return { newSlots, removedItem };
    }

    if (newSize === 'type_1_4' && currentSize === 'type_4') {
      const newSlots = [
        {},
        ...currentSlots.slice(0, currentSlotCount).map(createSlot),
      ];

      return { newSlots };
    }

    return { newSlots: currentSlots };
  }
}
